import React from 'react';

/**
 * 🎬📝 THE CREDITS CARD (09-23, MOVIE_NIGHT_UX_HANDOFF §3.7, Nina's ruling 16):
 * at the wrap, anyone who was in the event room or seated gets ONE card in the
 * feed dock (never a modal; the after-film chatter flows around it): three
 * taps + an optional line, Skip / Send, once per device. Modeled on
 * GenreVoteCard: pure, in flow, border-box, touch-action manipulation, ✕ =
 * "Not now" (hides for this visit only).
 *
 * `feedback` = the status rider `house.feedback`: { event_id, template, label,
 * room, phase, questions: [{ key, q, options: [label, ...] }] }.
 * `answers` = { [key]: optionIndex }. `state` ∈ 'idle' | 'sending' | 'thanks'.
 * Every action is a callback; the page owns the answers, the text, the POST
 * (utils/eventFeedback.submitEventFeedback) and the once-gate.
 * data-event-feedback / data-feedback-q / data-feedback-opt = grep markers.
 * ⚠️ No global box-sizing here · the input is 16px (no iOS zoom).
 */
export const FEEDBACK_TEXT_MAX = 1000;
export const FEEDBACK_THANKS = 'Got it. Thank you!';
export const FEEDBACK_PLACEHOLDER = 'Anything else? (optional)';

const isAnswered = (answers, q) => !!q && Number.isInteger(answers[q.key]) && answers[q.key] >= 0;

export default function EventFeedbackCard({ feedback, answers = {}, onAnswer, text = '', onText, onSend, onSkip, onDismiss, state = 'idle', THEME = {} }) {
  if (!feedback || !Array.isArray(feedback.questions) || !feedback.questions.length) return null;
  const T = THEME || {};
  const accent = T.accent || '#7b1fa2';
  const ink = T.text || '#eee';
  const muted = T.textMuted || '#999';
  const a = answers || {};
  const questions = feedback.questions.filter((q) => q && q.key && Array.isArray(q.options) && q.options.length);
  const answered = questions.some((q) => isAnswered(a, q));
  const hasText = !!String(text || '').trim();
  const sending = state === 'sending';
  const canSend = !sending && (answered || hasText);

  const root = {
    margin: '0 0 8px', padding: '10px 12px', borderRadius: 10, boxSizing: 'border-box', maxWidth: '100%',
    background: T.bgLight || 'rgba(20,16,30,0.92)', border: `1px solid ${accent}55`, boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
    color: ink,
  };

  if (state === 'thanks') {
    return (
      <div data-event-feedback="thanks" data-feedback-event={feedback.event_id} style={root}>
        <div style={{ fontSize: '0.9em', fontWeight: 700, overflowWrap: 'anywhere' }}>🎬 {FEEDBACK_THANKS}</div>
      </div>
    );
  }

  return (
    <div data-event-feedback={sending ? 'sending' : 'idle'} data-feedback-event={feedback.event_id} style={root}>
      <style>{'.event-fb-opt{touch-action:manipulation;-webkit-tap-highlight-color:transparent;transition:border-color 0.12s ease, background 0.12s ease, transform 0.08s ease} .event-fb-opt:active{transform:scale(0.985)}'}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.1em', color: accent }}>🎬📝 THE CREDITS</span>
        {onDismiss && (
          <button type="button" aria-label="Not now" title="Not now" onClick={onDismiss} disabled={sending}
            style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer', color: muted, fontSize: '0.85em', padding: '0 2px', lineHeight: 1 }}>
            ✕
          </button>
        )}
      </div>
      <div style={{ fontSize: '0.9em', fontWeight: 700, marginBottom: 8, overflowWrap: 'anywhere' }}>
        {feedback.label ? `${feedback.label}. How was it?` : 'How was it?'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {questions.map((q) => (
          <div key={q.key} data-feedback-q={q.key} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={{ fontSize: '0.8em', color: muted, overflowWrap: 'anywhere' }}>{q.prompt || q.q}</span>
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {q.options.map((label, index) => {
                const on = a[q.key] === index;
                return (
                  <button key={`${q.key}-${index}`} type="button" className="event-fb-opt" data-feedback-opt={index} aria-pressed={on} disabled={sending}
                    onClick={() => onAnswer && onAnswer(q.key, index)}
                    style={{
                      font: 'inherit', fontSize: '0.82em', fontWeight: on ? 800 : 600, padding: '5px 10px', borderRadius: 999, cursor: 'pointer', boxSizing: 'border-box',
                      background: on ? `${accent}44` : 'rgba(255,255,255,0.04)', border: `1px solid ${on ? accent : `${accent}40`}`, color: ink,
                      overflowWrap: 'anywhere',
                    }}>
                    {String(label)}
                  </button>
                );
              })}
            </span>
          </div>
        ))}
      </div>
      <input
        type="text"
        data-feedback-text="1"
        value={text || ''}
        maxLength={FEEDBACK_TEXT_MAX}
        placeholder={FEEDBACK_PLACEHOLDER}
        aria-label={FEEDBACK_PLACEHOLDER}
        autoComplete="off"
        disabled={sending}
        onChange={(e) => onText && onText(String(e.target.value || '').slice(0, FEEDBACK_TEXT_MAX))}
        onKeyDown={(e) => { if (e.key === 'Enter' && canSend && onSend) { e.preventDefault(); onSend(); } }}
        style={{
          font: 'inherit', fontSize: 16, width: '100%', boxSizing: 'border-box', marginTop: 9,
          padding: '7px 10px', borderRadius: 8, border: `1px solid ${accent}40`, background: 'rgba(255,255,255,0.04)', color: ink,
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <button type="button" data-feedback-skip="1" onClick={onSkip} disabled={sending}
          style={{ font: 'inherit', fontSize: '0.82em', fontWeight: 700, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', background: 'transparent', border: `1px solid ${accent}40`, color: muted }}>
          Skip
        </button>
        <button type="button" data-feedback-send="1" onClick={() => { if (canSend && onSend) onSend(); }} disabled={!canSend}
          style={{ font: 'inherit', fontSize: '0.82em', fontWeight: 800, padding: '6px 12px', borderRadius: 8, cursor: canSend ? 'pointer' : 'default', background: canSend ? accent : `${accent}33`, border: `1px solid ${accent}`, color: '#fff', opacity: canSend ? 1 : 0.6 }}>
          {sending ? 'Sending' : 'Send'}
        </button>
      </div>
    </div>
  );
}
